/**
 * /mail/sends — customer's view of every send (OCP-129).
 *
 * Filters: status (dropdown), since (date), recipient (substring).
 * Cursor-based pagination via "Load more". Filters reflected in URL
 * so deep-links + back-button work.
 *
 * Per-row click expands an inline detail panel with full subject +
 * message-id + tags + bounce reason if applicable. We deliberately
 * skip a separate /mail/sends/:id detail page for v1 — it's an extra
 * navigation hop with no extra data the inline panel doesn't show.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import { MailQuotaBanner } from '../components/MailQuotaBanner';
import { ApiError, api } from '../lib/api';
import { fmtDateTime } from '../lib/format-time';

interface Send {
  id: string;
  to: string;
  from: string | null;
  subject: string | null;
  status: string;
  region: string | null;
  message_id: string | null;
  rejected_reason: string | null;
  sent_at: string | null;
  tags: Record<string, string> | null;
}

interface SendsResponse {
  oms_configured: boolean;
  customer_exists: boolean;
  sends: Send[];
  cursor: string | null;
}

const STATUS_OPTIONS = [
  '',
  'queued',
  'sent',
  'delivered',
  'bounced',
  'complained',
  'rejected',
  'failed',
] as const;

export function MailSendsPage() {
  const { t } = useTranslation('mail-sends');
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState(params.get('status') ?? '');
  const [recipient, setRecipient] = useState(params.get('recipient') ?? '');
  const [since, setSince] = useState(params.get('since') ?? '');
  // Cursor is internal state — not URL-mirrored (each "Load more"
  // would create a noisy back-button history otherwise).
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  // Accumulate rows across "Load more" calls.
  const [allSends, setAllSends] = useState<Send[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [state, setState] = useState<'loading' | 'idle' | 'error'>('loading');
  const [response, setResponse] = useState<SendsResponse | null>(null);
  const [errorDetail, setErrorDetail] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Reflect filters in URL.
  useEffect(() => {
    const p = new URLSearchParams();
    if (status) p.set('status', status);
    if (recipient) p.set('recipient', recipient);
    if (since) p.set('since', since);
    setParams(p, { replace: true });
    // Filter change resets cursor + accumulated rows.
    setCursor(undefined);
    setAllSends([]);
    setExpandedId(null);
  }, [status, recipient, since, setParams]);

  const qs = useMemo(() => {
    const p = new URLSearchParams();
    if (status) p.set('status', status);
    if (recipient) p.set('recipient', recipient);
    if (since) p.set('since', since);
    if (cursor) p.set('cursor', cursor);
    p.set('limit', '50');
    return p.toString();
  }, [status, recipient, since, cursor]);

  const load = useCallback(async () => {
    setState('loading');
    setErrorDetail(null);
    try {
      const r = await api<SendsResponse>(`/v1/portal/mail/sends?${qs}`);
      setResponse(r);
      // Append on "load more" (cursor present), replace on filter
      // change (cursor undefined).
      setAllSends((prev) => (cursor ? [...prev, ...r.sends] : r.sends));
      setNextCursor(r.cursor);
      setState('idle');
    } catch (err) {
      if (err instanceof ApiError) {
        setErrorDetail(
          err.status === 502 ? t('error.unreachable') : t('error.http', { status: err.status }),
        );
      } else {
        setErrorDetail(t('error.generic'));
      }
      setState('error');
    }
  }, [qs, cursor, t]);

  useEffect(() => {
    void load();
  }, [load]);

  if (response && !response.oms_configured) {
    return (
      <div className="rounded-lg border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-900/30 p-5 text-sm text-amber-900 dark:text-amber-200">
        <h2 className="text-base font-semibold">{t('unavailable.title')}</h2>
        <p className="mt-1">{t('unavailable.text')}</p>
      </div>
    );
  }
  if (response && !response.customer_exists) {
    return <NoMailAccountEmpty />;
  }

  return (
    <div>
      <MailQuotaBanner />
      <Filters
        status={status}
        setStatus={setStatus}
        recipient={recipient}
        setRecipient={setRecipient}
        since={since}
        setSince={setSince}
      />

      {state === 'error' && (
        <div
          role="alert"
          className="mb-4 rounded-lg border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-800 dark:text-red-300"
        >
          {errorDetail}
        </div>
      )}

      {state === 'idle' && allSends.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
          <table className="w-full min-w-[640px] text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800 text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-3 py-2">{t('table.sent')}</th>
                <th className="px-3 py-2">{t('table.to')}</th>
                <th className="px-3 py-2">{t('table.subject')}</th>
                <th className="px-3 py-2">{t('table.status')}</th>
                <th className="px-3 py-2">{t('table.domain')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {allSends.map((s) => (
                <SendRow
                  key={s.id}
                  send={s}
                  expanded={expandedId === s.id}
                  onToggle={() =>
                    setExpandedId((p) => (p === s.id ? null : s.id))
                  }
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {nextCursor && (
        <div className="mt-4 flex justify-center">
          <button
            type="button"
            disabled={state === 'loading'}
            onClick={() => setCursor(nextCursor)}
            className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-1.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-800/60 disabled:bg-gray-100 dark:disabled:bg-gray-800/60 disabled:text-gray-400"
          >
            {state === 'loading' ? t('loading') : t('loadMore')}
          </button>
        </div>
      )}
    </div>
  );
}

function Filters({
  status,
  setStatus,
  recipient,
  setRecipient,
  since,
  setSince,
}: {
  status: string;
  setStatus: (v: string) => void;
  recipient: string;
  setRecipient: (v: string) => void;
  since: string;
  setSince: (v: string) => void;
}) {
  const { t } = useTranslation('mail-sends');
  return (
    <div className="mb-4 flex flex-wrap items-end gap-3">
      <label className="flex flex-col gap-1 text-xs">
        <span className="text-gray-600 dark:text-gray-400">{t('filters.status')}</span>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm"
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o} value={o}>
              {o ? t(`status.${o}`, { defaultValue: o }) : t('filters.all')}
            </option>
          ))}
        </select>
      </label>
      <label className="flex flex-col gap-1 text-xs">
        <span className="text-gray-600 dark:text-gray-400">{t('filters.recipient')}</span>
        <input
          type="search"
          value={recipient}
          onChange={(e) => setRecipient(e.target.value)}
          placeholder={t('filters.recipientPlaceholder')}
          className="w-56 rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm"
        />
      </label>
      <label className="flex flex-col gap-1 text-xs">
        <span className="text-gray-600 dark:text-gray-400">{t('filters.since')}</span>
        <input
          type="date"
          value={since}
          onChange={(e) => setSince(e.target.value)}
          className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm"
        />
      </label>
    </div>
  );
}

function SendRow({
  send,
  expanded,
  onToggle,
}: {
  send: Send;
  expanded: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation('mail-sends');
  return (
    <>
      <tr
        onClick={onToggle}
        className={`cursor-pointer ${expanded ? 'bg-brand-50/50' : 'hover:bg-gray-50 dark:hover:bg-gray-800/60'}`}
      >
        <td className="whitespace-nowrap px-3 py-2 text-xs tabular-nums text-gray-500">
          {send.sent_at ? formatTime(send.sent_at) : '—'}
        </td>
        <td className="truncate px-3 py-2 text-gray-700 dark:text-gray-300">{send.to}</td>
        <td className="truncate px-3 py-2 font-medium text-gray-900 dark:text-gray-100">
          {send.subject ?? t('table.noSubject')}
        </td>
        <td className="px-3 py-2">
          <StatusPill status={send.status} />
        </td>
        <td className="px-3 py-2 text-xs text-gray-500">
          {senderDomain(send.from)}
        </td>
      </tr>
      {expanded && (
        <tr className="bg-gray-50 dark:bg-gray-800">
          <td colSpan={5} className="px-3 py-3">
            <DetailPanel send={send} />
          </td>
        </tr>
      )}
    </>
  );
}

function DetailPanel({ send }: { send: Send }) {
  const { t } = useTranslation('mail-sends');
  return (
    <div className="space-y-2 text-xs">
      {send.from && (
        <Row label={t('detail.from')}>
          <code className="rounded bg-white dark:bg-gray-900 px-1.5 py-0.5">{send.from}</code>
        </Row>
      )}
      {send.message_id && (
        <Row label={t('detail.messageId')}>
          <code className="rounded bg-white dark:bg-gray-900 px-1.5 py-0.5">{send.message_id}</code>
        </Row>
      )}
      {send.rejected_reason && (
        <Row label={t('detail.reason')}>
          <span className="text-red-700 dark:text-red-300">{send.rejected_reason}</span>
        </Row>
      )}
      {send.tags && Object.keys(send.tags).length > 0 && (
        <Row label={t('detail.tags')}>
          <span className="flex flex-wrap gap-1">
            {Object.entries(send.tags).map(([k, v]) => (
              <span
                key={k}
                className="rounded bg-white dark:bg-gray-900 px-1.5 py-0.5 text-gray-700 dark:text-gray-300"
              >
                {k}={String(v)}
              </span>
            ))}
          </span>
        </Row>
      )}
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline gap-3">
      <span className="w-24 shrink-0 text-gray-500">{label}</span>
      <span className="min-w-0 flex-1">{children}</span>
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const { t } = useTranslation('mail-sends');
  const tone =
    status === 'delivered'
      ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300 border-emerald-200 dark:border-emerald-900/50'
      : status === 'bounced' || status === 'rejected' || status === 'failed'
        ? 'bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-300 border-red-200 dark:border-red-900/50'
        : status === 'complained'
          ? 'bg-amber-50 dark:bg-amber-900/30 text-amber-900 dark:text-amber-200 border-amber-200 dark:border-amber-900/50'
          : 'bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-800';
  return (
    <span className={`inline-flex rounded border px-1.5 py-0.5 text-xs ${tone}`}>
      {t(`status.${status}`, { defaultValue: status })}
    </span>
  );
}

function NoMailAccountEmpty() {
  const { t } = useTranslation('mail-sends');
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 text-center text-sm shadow-sm">
      <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">{t('empty.title')}</h2>
      <p className="mt-2 text-gray-600 dark:text-gray-400">{t('empty.text')}</p>
      <div className="mt-4 flex justify-center gap-3">
        <Link
          to="/mail/api-keys"
          className="rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600"
        >
          {t('empty.apiKey')}
        </Link>
        <Link
          to="/mail/connections"
          className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
        >
          {t('empty.connect')}
        </Link>
      </div>
    </div>
  );
}

function EmptyState() {
  const { t } = useTranslation('mail-sends');
  return (
    <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-6 text-center text-sm text-gray-500">
      {t('empty.noMatch')}
    </div>
  );
}

function formatTime(iso: string): string {
  return fmtDateTime(iso, iso);
}

// OCP-278 — the sending domain a customer cares about, derived from the
// From address. Handles bare `user@domain` and display-name forms like
// `orboto <hello@orboto.io>`. Falls back to `—` when absent / malformed.
function senderDomain(from: string | null): string {
  if (!from) return '—';
  const at = from.lastIndexOf('@');
  if (at === -1) return '—';
  const domain = from
    .slice(at + 1)
    .replace(/[>)\s]+$/, '')
    .trim();
  return domain.length > 0 ? domain : '—';
}
